<script setup lang="ts">
import { ref } from 'vue';
import { useAdminEditsStore } from '@/stores/adminEdits';
import { LockIcon, KeyIcon, CheckIcon, XIcon, ShieldCheckIcon } from 'vue-tabler-icons';

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'logged-in'): void;
}>();

const adminEditsStore = useAdminEditsStore();

const pin = ref('');
const error = ref<string | null>(null);
const success = ref<string | null>(null);

function closeDialog() {
  emit('update:modelValue', false);
  pin.value = '';
  error.value = null;
  success.value = null;
}

function handleLogin() {
  error.value = null;
  success.value = null;

  if (!pin.value.trim()) {
    error.value = 'કૃપા કરીને એડમિન પિન (PIN) દાખલ કરો.';
    return;
  }

  const res = adminEditsStore.loginAdmin(pin.value.trim());
  if (!res.success) {
    error.value = res.message;
  } else {
    success.value = 'સ્વાગત છે, એડમિન!';
    setTimeout(() => {
      emit('logged-in');
      closeDialog();
    }, 600);
  }
}
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="400"
    persistent
    @update:model-value="(val) => emit('update:modelValue', val)"
  >
    <v-card class="rounded-xl overflow-hidden shadow-24">
      <v-card-item class="bg-primary text-white py-3 px-4">
        <div class="d-flex align-center justify-space-between w-100">
          <div class="d-flex align-center" style="gap: 10px;">
            <v-avatar size="32" color="white" class="text-primary">
              <LockIcon size="18" class="text-primary" />
            </v-avatar>
            <div class="text-subtitle-1 font-weight-black">
              એડમિન લૉગિન (Admin Access)
            </div>
          </div>
          <v-btn icon size="small" variant="text" color="white" @click="closeDialog">
            <XIcon size="18" />
          </v-btn>
        </div>
      </v-card-item>

      <v-card-text class="pa-4 text-center">
        <v-avatar size="56" color="lightprimary" class="mb-3">
          <ShieldCheckIcon size="30" class="text-primary" />
        </v-avatar>

        <h3 class="text-subtitle-1 font-weight-black mb-1">
          એડમિન વેરિફિકેશન
        </h3>
        <p class="text-caption text-medium-emphasis mb-4">
          સુધારાઓ ચકાસવા અને મંજૂર કરવા માટે એડમિન પિન દાખલ કરો.
        </p>

        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="compact"
          class="rounded-lg mb-3 text-caption"
        >
          {{ error }}
        </v-alert>

        <v-alert
          v-if="success"
          type="success"
          variant="flat"
          density="compact"
          class="rounded-lg mb-3 text-caption text-white font-weight-bold"
        >
          {{ success }}
        </v-alert>

        <v-text-field
          v-model="pin"
          type="password"
          label="એડમિન PIN"
          placeholder="••••"
          variant="outlined"
          density="comfortable"
          maxlength="8"
          autofocus
          class="mb-2"
          @keyup.enter="handleLogin"
        >
          <template #prepend-inner>
            <KeyIcon size="18" class="text-medium-emphasis mr-1" />
          </template>
        </v-text-field>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-3 d-flex justify-end" style="gap: 8px;">
        <v-btn variant="tonal" color="secondary" rounded="lg" size="small" @click="closeDialog">
          રદ કરો
        </v-btn>
        <v-btn color="primary" variant="flat" rounded="lg" size="small" @click="handleLogin">
          <CheckIcon size="16" class="mr-1" />
          લૉગિન કરો
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
